import React from 'react';
import { T } from '../../assets/theme';

// ── Card ──────────────────────────────────────────────────────────────────────
export function Card({ children, style = {}, title }) {
  return (
    <div className="dg-panel" style={{ padding: '20px 24px', marginBottom: 14, ...style }}>
      {title && <SectionTitle>{title}</SectionTitle>}
      {children}
    </div>
  );
}

// ── SectionTitle ─────────────────────────────────────────────────────────────
export function SectionTitle({ children }) {
  return (
    <div style={{
      color: '#5F98C4',
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: '1.2px',
      textTransform: 'uppercase',
      borderBottom: '1px solid rgba(255,255,255,0.05)',
      paddingBottom: 8,
      marginBottom: 14,
    }}>
      {children}
    </div>
  );
}

// ── Badge ─────────────────────────────────────────────────────────────────────
export function Badge({ status }) {
  const map = {
    penetrated: { bg: 'rgba(232,64,64,0.12)',  border: 'rgba(232,64,64,0.35)',  text: '#e84040',              label: 'PENETRATED' },
    critical:   { bg: 'rgba(255,23,68,0.14)',  border: 'rgba(255,23,68,0.4)',   text: '#FF1744',              label: 'CRITICAL'   },
    clear:      { bg: 'rgba(62,169,109,0.10)', border: 'rgba(62,169,109,0.3)',  text: 'rgba(62,169,109,0.9)', label: 'CLEAR'      },
    outside:    { bg: 'rgba(172,190,202,0.06)',border: 'rgba(172,190,202,0.2)', text: '#ACBECA',              label: 'OUTSIDE'    },
    info:       { bg: 'rgba(95,152,196,0.10)', border: 'rgba(95,152,196,0.3)',  text: '#5F98C4',              label: 'INFO'       },
    error:      { bg: 'rgba(198,167,68,0.10)', border: 'rgba(198,167,68,0.3)',  text: '#C6A744',              label: 'ERROR'      },
  };
  const s = map[status] || map.error;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '3px 9px', borderRadius: 99,
      border: `1px solid ${s.border}`,
      background: s.bg, color: s.text,
      fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
}

// ── StatCard ──────────────────────────────────────────────────────────────────
export function StatCard({ label, value, icon, color, sub }) {
  const c = color || '#5F98C4';
  return (
    <div className="dg-kpi" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: 'var(--text-tertiary)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}>
          {label}
        </span>
        {icon && <span style={{ fontSize: 18, color: c }}>{icon}</span>}
      </div>
      <div style={{ color: c, fontSize: 36, fontWeight: 700, lineHeight: 1, letterSpacing: '-1px' }}>
        {value}
      </div>
      {sub && <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>{sub}</div>}
    </div>
  );
}

// ── FormInput ─────────────────────────────────────────────────────────────────
export function FormInput({ label, value, onChange, type = 'text', step, min, max,
                            options, placeholder, disabled = false }) {
  return (
    <div>
      {label && (
        <div style={{ color: 'var(--text-tertiary)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
          {label}
        </div>
      )}
      {options ? (
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          className="dg-input"
          style={{ cursor: 'pointer', opacity: disabled ? 0.5 : 1 }}
        >
          {options.map(o => (
            <option key={o.value ?? o} value={o.value ?? o}>
              {o.label ?? o}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={type} value={value} step={step} min={min} max={max}
          placeholder={placeholder} disabled={disabled}
          onChange={e => onChange(e.target.value)}
          className="dg-input"
          style={{ opacity: disabled ? 0.5 : 1 }}
        />
      )}
    </div>
  );
}

// ── FormRow (two-column grid) ─────────────────────────────────────────────────
export function FormRow({ children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
      {children}
    </div>
  );
}

// ── ResultRow ─────────────────────────────────────────────────────────────────
export function ResultRow({ name, status, surf_h, obs_h, margin, slope, ref: refStr, check }) {
  const pen = status === 'penetrated' || status === 'critical';
  const rowBg = pen ? 'rgba(232,64,64,0.06)' : status === 'clear' ? 'rgba(62,169,109,0.04)' : 'rgba(95,152,196,0.04)';
  const borderC = pen ? 'rgba(232,64,64,0.22)' : status === 'clear' ? 'rgba(62,169,109,0.14)' : 'rgba(95,152,196,0.12)';
  return (
    <div style={{
      background: rowBg, borderRadius: 10,
      padding: '10px 14px', marginBottom: 6,
      border: `1px solid ${borderC}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
        <span style={{ color: 'var(--text-primary)', fontSize: 12, fontWeight: 600 }}>{name ?? check}</span>
        <Badge status={status} />
      </div>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 11 }}>
        {surf_h !== undefined && (
          <span style={{ color: 'var(--text-tertiary)' }}>
            Surface H: <span style={{ color: 'var(--text-primary)' }}>{surf_h}m AGL</span>
          </span>
        )}
        {obs_h !== undefined && (
          <span style={{ color: 'var(--text-tertiary)' }}>
            Obstacle H: <span style={{ color: pen ? '#e84040' : 'var(--text-primary)' }}>{obs_h}m AGL</span>
          </span>
        )}
        {margin !== undefined && (
          <span style={{ color: 'var(--text-tertiary)' }}>
            Margin: <span style={{ color: pen ? '#e84040' : 'rgba(62,169,109,0.9)', fontWeight: 700 }}>
              {typeof margin === 'number' ? margin.toFixed(1) + 'm' : margin}
            </span>
          </span>
        )}
        {slope && (
          <span style={{ color: 'var(--text-tertiary)' }}>
            Slope: <span style={{ color: '#5F98C4' }}>{slope}</span>
          </span>
        )}
        {refStr && (
          <span style={{ color: 'var(--text-tertiary)', fontStyle: 'italic', fontSize: 10 }}>{refStr}</span>
        )}
      </div>
    </div>
  );
}

// ── Spinner ───────────────────────────────────────────────────────────────────
export function Spinner() {
  return (
    <div style={{ textAlign: 'center', padding: 28, color: 'var(--text-tertiary)', fontSize: 13 }}>
      <div style={{
        display: 'inline-block', width: 28, height: 28,
        border: '2px solid rgba(255,255,255,0.08)',
        borderTop: '2px solid #5F98C4',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
        marginBottom: 8,
      }} />
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
      <div>Loading…</div>
    </div>
  );
}

// ── EmptyState ────────────────────────────────────────────────────────────────
export function EmptyState({ message = 'No data yet.', icon = '⊘' }) {
  return (
    <div style={{ textAlign: 'center', padding: 36, color: 'var(--text-tertiary)' }}>
      <div style={{ fontSize: 30, marginBottom: 10, opacity: 0.5 }}>{icon}</div>
      <div style={{ fontSize: 13 }}>{message}</div>
    </div>
  );
}

// ── Alert banner ──────────────────────────────────────────────────────────────
export function Alert({ type = 'info', children }) {
  const colors = {
    info:    { bg: 'rgba(95,152,196,0.08)',  border: 'rgba(95,152,196,0.3)',   c: '#5F98C4'              },
    success: { bg: 'rgba(62,169,109,0.08)',  border: 'rgba(62,169,109,0.3)',   c: 'rgba(62,169,109,0.9)' },
    warning: { bg: 'rgba(198,167,68,0.08)',  border: 'rgba(198,167,68,0.3)',   c: '#C6A744'              },
    danger:  { bg: 'rgba(232,64,64,0.08)',   border: 'rgba(232,64,64,0.3)',    c: '#e84040'              },
  };
  const s = colors[type] || colors.info;
  return (
    <div style={{
      background: s.bg, border: `1px solid ${s.border}`,
      borderRadius: 10, padding: '10px 14px',
      color: s.c, fontSize: 12, marginBottom: 12,
    }}>
      {children}
    </div>
  );
}

// ── Confirm Modal ─────────────────────────────────────────────────────────────
export function ConfirmModal({ title, message, onConfirm, onCancel, confirmText = 'Confirm', cancelText = 'Cancel', variant = 'danger' }) {
  const isDanger = variant === 'danger';
  return (
    <div className="dg-modal-overlay">
      <div className="dg-modal" style={{ padding: '32px 40px', width: 440, maxWidth: '90%' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 52, height: 52, borderRadius: 16, margin: '0 auto 20px',
            background: isDanger ? 'rgba(232,64,64,0.12)' : 'var(--blue-glass-fill)',
            border: `1px solid ${isDanger ? 'rgba(232,64,64,0.3)' : 'var(--blue-glass-border)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22,
          }}>
            {isDanger ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="#e84040" strokeWidth="2" width="22" height="22">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="#5F98C4" strokeWidth="2" width="22" height="22">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            )}
          </div>
          <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10 }}>{title}</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 28, lineHeight: 1.6 }}>{message}</div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={onCancel}
              className="dg-btn dg-btn-ghost"
              style={{ flex: 1, padding: '10px 16px' }}
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className="dg-btn"
              style={{
                flex: 1, padding: '10px 16px',
                background: isDanger ? '#e84040' : '#2C72AE',
                color: 'white', border: 'none',
                boxShadow: isDanger ? '0 4px 12px rgba(232,64,64,0.35)' : '0 4px 12px rgba(44,114,174,0.35)',
              }}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Keep T export for pages that still reference T directly
export { T };

/**
 * Drag handle for resizing a panel. `variant="bar"` is a full-width bar
 * (e.g. the top edge of a bottom-docked results drawer, ns-resize cursor).
 * `variant="corner"` is a small bottom-right grip (e.g. a floating modal,
 * nwse-resize cursor).
 */
export function ResizeHandle({ onMouseDown, variant = 'bar' }) {
  if (variant === 'corner') {
    return (
      <div
        onMouseDown={onMouseDown}
        title="Drag to resize"
        style={{
          position: 'absolute', right: 0, bottom: 0, width: 18, height: 18,
          cursor: 'nwse-resize', zIndex: 10,
          background: 'linear-gradient(135deg, transparent 50%, var(--glass-border) 50%, var(--glass-border) 60%, transparent 60%, transparent 70%, var(--glass-border) 70%, var(--glass-border) 80%, transparent 80%)',
        }}
      />
    );
  }
  return (
    <div
      onMouseDown={onMouseDown}
      title="Drag to resize"
      style={{
        height: 7, width: '100%', cursor: 'ns-resize', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div style={{ width: 36, height: 3, borderRadius: 2, background: 'var(--glass-border)' }} />
    </div>
  );
}

export function DraggableModal({ title, onClose, children, width = 750, height = 450 }) {
  const [pos, setPos] = React.useState({ x: 100, y: 100 });
  const [size, setSize] = React.useState({ width, height });
  const [dragging, setDragging] = React.useState(false);
  const [resizing, setResizing] = React.useState(false);
  const dragStart = React.useRef({ x: 0, y: 0 });
  const resizeStart = React.useRef({ x: 0, y: 0, width, height });
  const modalRef = React.useRef(null);

  React.useEffect(() => {
    // Center the modal in the viewport on mount
    const pw = window.innerWidth;
    const ph = window.innerHeight;
    setPos({
      x: Math.max(20, (pw - width) / 2),
      y: Math.max(20, (ph - height) / 2)
    });
  }, [width, height]);

  const onMouseDown = (e) => {
    // Left click only
    if (e.button !== 0) return;
    // Don't drag if clicking a button or interactives
    if (e.target.tagName === 'BUTTON' || e.target.closest('button') || e.target.tagName === 'SELECT' || e.target.tagName === 'INPUT') return;
    
    setDragging(true);
    dragStart.current = {
      x: e.clientX - pos.x,
      y: e.clientY - pos.y
    };
    e.preventDefault();
  };

  React.useEffect(() => {
    if (!dragging) return;

    const onMouseMove = (e) => {
      const newX = e.clientX - dragStart.current.x;
      const newY = e.clientY - dragStart.current.y;
      
      // Keep within boundaries
      const maxW = window.innerWidth - 60;
      const maxH = window.innerHeight - 60;
      setPos({
        x: Math.min(Math.max(10, newX), maxW),
        y: Math.min(Math.max(10, newY), maxH)
      });
    };

    const onMouseUp = () => {
      setDragging(false);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [dragging]);

  const onResizeMouseDown = (e) => {
    if (e.button !== 0) return;
    setResizing(true);
    resizeStart.current = { x: e.clientX, y: e.clientY, width: size.width, height: size.height };
    e.preventDefault();
    e.stopPropagation();
  };

  React.useEffect(() => {
    if (!resizing) return;

    const onMouseMove = (e) => {
      const dx = e.clientX - resizeStart.current.x;
      const dy = e.clientY - resizeStart.current.y;
      setSize({
        width: Math.min(window.innerWidth - pos.x - 10, Math.max(360, resizeStart.current.width + dx)),
        height: Math.min(window.innerHeight - pos.y - 10, Math.max(220, resizeStart.current.height + dy)),
      });
    };

    const onMouseUp = () => setResizing(false);

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [resizing, pos.x, pos.y]);

  return (
    <div
      ref={modalRef}
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        width: size.width,
        height: size.height,
        zIndex: 5000,
        background: 'rgba(7, 17, 30, 0.94)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid var(--glass-border)',
        borderRadius: 12,
        boxShadow: '0 24px 64px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.06)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div
        onMouseDown={onMouseDown}
        style={{
          padding: '12px 20px',
          background: 'rgba(255, 255, 255, 0.03)',
          borderBottom: '1px solid var(--glass-border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: dragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          flexShrink: 0,
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14 }}>📊</span> {title}
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid var(--glass-border)',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
            fontSize: 11,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            borderRadius: 6,
            transition: 'all 0.15s ease',
          }}
          title="Close window"
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(232, 64, 64, 0.2)'; e.currentTarget.style.color = '#e84040'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
        >
          ✕
        </button>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: 20, color: 'var(--text-primary)' }}>
        {children}
      </div>
      <ResizeHandle variant="corner" onMouseDown={onResizeMouseDown} />
    </div>
  );
}

